/**
 * AdTelemetry — sends ad analytics events to the ZZAZZ analytics backend.
 *
 * Ported from timepay-ui. Key differences:
 *  - Endpoint read from REACT_APP_ANALYTICS_ENDPOINT (CRA env var)
 *  - No Vite / TIMEPAY_API_ENDPOINT dependency
 *
 * Usage:
 *   import adTelemetry from './AdTelemetry';
 *   adTelemetry.setPageId('abc');
 *   adTelemetry.setDeviceId('xyz');
 *   ...
 *   adTelemetry.ensureImpression({ adId, timePayVariant });
 *   adTelemetry.adProgressEvent({ adId, action: 'start', time: 0, timePayVariant });
 */

const ANALYTICS_ENDPOINT = process.env.REACT_APP_ANALYTICS_ENDPOINT || '';

class AdTelemetry {
  constructor(eventsEndpoint) {
    this.eventsEndpoint = eventsEndpoint;

    // pageId control
    this.pageId = null;
    this._resolvePageId = null;
    this.pageIdPromise = new Promise((res) => (this._resolvePageId = res));

    this.deviceId = null;
    this._resolveDeviceId = null;
    this.deviceIdPromise = new Promise((res) => (this._resolveDeviceId = res));

    this.userId = null;
    this._resolveUserId = null;
    this.userIdPromise = new Promise((res) => (this._resolveUserId = res));

    this.trackingId = null;
    this._resolveTrackingId = null;
    this.trackingIdPromise = new Promise((res) => (this._resolveTrackingId = res));

    this.sessionId = null;
    this._resolveSessionId = null;
    this.sessionIdPromise = new Promise((res) => (this._resolveSessionId = res));

    this.platform = null;
    this.appVersion = null;
    this.featureSet = null;

    // impression state
    this.adImpressionId = null;
    this.adImpressionPromise = null;

    this.articleUrlOrId = null;

    // queued events until all required ids + impressionId exist
    this.queue = [];
    // queued events that do NOT require an adImpressionId
    this.preImpressionQueue = [];
  }

  setPageId(id) {
    if (!id) return;
    this.pageId = id;
    if (this._resolvePageId) {
      this._resolvePageId(id);
      this._resolvePageId = null;
    }
    this.flushPreImpressionQueue();
    if (this.adImpressionId) this.flushQueue();
  }

  setArticleUrlOrId(urlOrId) {
    this.articleUrlOrId = urlOrId;
  }

  setDeviceId(id) {
    if (!id) return;
    this.deviceId = id;
    if (this._resolveDeviceId) {
      this._resolveDeviceId(id);
      this._resolveDeviceId = null;
    }
    this.flushPreImpressionQueue();
  }

  setUserId(id) {
    if (!id) return;
    this.userId = id;
    if (this._resolveUserId) {
      this._resolveUserId(id);
      this._resolveUserId = null;
    }
    this.flushPreImpressionQueue();
  }

  setTrackingId(id) {
    if (!id) return;
    this.trackingId = id;
    if (this._resolveTrackingId) {
      this._resolveTrackingId(id);
      this._resolveTrackingId = null;
    }
    this.flushPreImpressionQueue();
  }

  setSessionId(id) {
    if (!id) return;
    this.sessionId = id;
    if (this._resolveSessionId) {
      this._resolveSessionId(id);
      this._resolveSessionId = null;
    }
    this.flushPreImpressionQueue();
  }

  setPlatform(platform) {
    if (!platform) return;
    this.platform = platform;
  }

  setAppVersion(appVersion) {
    if (!appVersion) return;
    this.appVersion = appVersion;
  }

  setFeatureSet(featureSet) {
    if (!featureSet) return;
    this.featureSet = featureSet;
  }

  async sendEvent(payload) {
    const results = await Promise.allSettled([
      fetch(this.eventsEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'tracking-id': this.trackingId || '',
          'user-id':     this.userId     || '',
          'session-id':  this.sessionId  || '',
        },
        body: JSON.stringify(payload),
        keepalive: true,
      }),
    ]);

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error(`[AdTelemetry] endpoint ${index} error:`, result.reason);
      }
    });

    if (results[0].status === 'fulfilled') {
      try {
        const response = results[0].value;
        if (!response.ok) {
          console.error(`[AdTelemetry] endpoint returned status: ${response.status}`);
          return null;
        }
        const data = await response.json();
        return data?.event_id ?? null;
      } catch (error) {
        console.error('[AdTelemetry] Error parsing response:', error);
        return null;
      }
    }

    return null;
  }

  // ---- Impression gating ------------------------------------------------------

  ensureImpression(args) {
    return this.ensureAdImpression(args);
  }

  async ensureAdImpression({ adId, timePayVariant }) {
    if (this.adImpressionId) return this.adImpressionId;
    if (this.adImpressionPromise) return this.adImpressionPromise;

    this.adImpressionPromise = (async () => {
      await Promise.all([
        this.pageIdPromise,
        this.deviceIdPromise,
        this.userIdPromise,
        this.trackingIdPromise,
        this.sessionIdPromise,
      ]);

      const payload = {
        type:         'ad',
        pageId:       this.pageId,
        platform:     this.platform,
        appVersion:   this.appVersion,
        featureSet:   this.featureSet,
        subtype:      'impression',
        pageUrl:      this.articleUrlOrId,
        adId:         String(adId),
        position:     'overlay',
        adVariantId:  timePayVariant,
      };

      const eventId = await this.sendEvent(payload);
      this.adImpressionId = eventId;
      await this.flushQueue();
      return this.adImpressionId;
    })();

    return this.adImpressionPromise;
  }

  // ---- Queueing ---------------------------------------------------------------

  async queueOrSend(eventFn, args) {
    if (
      this.pageId && this.adImpressionId &&
      this.deviceId && this.userId && this.trackingId && this.sessionId
    ) return eventFn.call(this, args);
    this.queue.push({ eventFn, args });
  }

  async queueOrSendPreImpression(eventFn, args) {
    if (
      this.pageId && this.deviceId &&
      this.userId && this.trackingId && this.sessionId
    ) return eventFn.call(this, args);
    this.preImpressionQueue.push({ eventFn, args });
  }

  async flushQueue() {
    if (
      !this.pageId || !this.adImpressionId ||
      !this.deviceId || !this.userId || !this.trackingId || !this.sessionId
    ) return;
    const q = this.queue;
    this.queue = [];
    for (const { eventFn, args } of q) {
      try { await eventFn.call(this, args); } catch {}
    }
  }

  async flushPreImpressionQueue() {
    if (
      !this.pageId || !this.deviceId ||
      !this.userId || !this.trackingId || !this.sessionId
    ) return;
    const q = this.preImpressionQueue;
    this.preImpressionQueue = [];
    for (const { eventFn, args } of q) {
      try { await eventFn.call(this, args); } catch {}
    }
  }

  // ---- Concrete event senders -------------------------------------------------

  async _adProgress({ adId, action, time, timePayVariant }) {
    const payload = {
      type:            'ad',
      pageId:          this.pageId,
      platform:        this.platform,
      appVersion:      this.appVersion,
      featureSet:      this.featureSet,
      adImpressionId:  this.adImpressionId,
      subtype:         'progress',
      pageUrl:         this.articleUrlOrId,
      adId:            String(adId),
      action,
      time,
      label:           '',
      adVariantId:     timePayVariant,
    };
    return this.sendEvent(payload);
  }

  async _adInteraction({ adId, action, time, timePayVariant, label }) {
    const payload = {
      type:            'ad',
      pageId:          this.pageId,
      platform:        this.platform,
      appVersion:      this.appVersion,
      featureSet:      this.featureSet,
      adImpressionId:  this.adImpressionId,
      subtype:         'interaction',
      pageUrl:         this.articleUrlOrId,
      adId:            String(adId),
      action,
      time,
      label:           label || '',
      adVariantId:     timePayVariant,
    };
    return this.sendEvent(payload);
  }

  async _adDisengagement({ adId, action, time, timePayVariant }) {
    const payload = {
      type:            'ad',
      pageId:          this.pageId,
      platform:        this.platform,
      appVersion:      this.appVersion,
      featureSet:      this.featureSet,
      adImpressionId:  this.adImpressionId,
      subtype:         'disengagement',
      pageUrl:         this.articleUrlOrId,
      adId:            String(adId),
      action,
      time,
      label:           '',
      adVariantId:     timePayVariant,
    };
    return this.sendEvent(payload);
  }

  async _adError({ adId, errorCode, errorMessage }) {
    const payload = {
      type:            'ad',
      pageId:          this.pageId,
      platform:        this.platform,
      appVersion:      this.appVersion,
      featureSet:      this.featureSet,
      adImpressionId:  this.adImpressionId,
      subtype:         'error',
      pageUrl:         this.articleUrlOrId,
      adId:            String(adId),
      errorCode,
      errorMessage,
      severity:        'warning',
    };
    return this.sendEvent(payload);
  }

  async _adLifecycle({ action, adId, timePayVariant, label }) {
    const payload = {
      type:        'ad',
      pageId:      this.pageId,
      platform:    this.platform,
      appVersion:  this.appVersion,
      featureSet:  this.featureSet,
      subtype:     'lifecycle',
      pageUrl:     this.articleUrlOrId,
      adId:        String(adId ?? 'unknown'),
      action,
      label:       label || '',
      adVariantId: timePayVariant,
    };
    if (this.adImpressionId) payload.adImpressionId = this.adImpressionId;
    return this.sendEvent(payload);
  }

  // ---- Public wrappers --------------------------------------------------------

  adProgressEvent(args)      { return this.queueOrSend(this._adProgress,      args); }
  adInteractionEvent(args)   { return this.queueOrSend(this._adInteraction,   args); }
  adDisengagementEvent(args) { return this.queueOrSend(this._adDisengagement, args); }
  adErrorEvent(args)         { return this.queueOrSend(this._adError,         args); }
  adLifecycleEvent(args)     { return this.queueOrSendPreImpression(this._adLifecycle, args); }
}

const adTelemetry = new AdTelemetry(ANALYTICS_ENDPOINT);
export default adTelemetry;
